import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useState, useEffect, MouseEvent } from 'react'
import { Checkbox, IconButton, Divider, Popover } from '@mui/material'

import { Document } from '../../types/document.types'
import { UserSessionType } from '../../types/user.types'
import TrashIcon from '../../assets/icons/trash-icon.svg'
import { numberWithCommas } from '../../utils/common.utils'
import GroupsIcon from '../../assets/icons/groups-icon.svg'
import MoreMenuIcon from '../../assets/icons/more-menu-icon.svg'
import ArrowRightIcon from '../../assets/icons/arrow-right-icon.svg'

const DocumentsReadyListItem = ({
  userSession,
  documentData,
  processingDocument,
  handleGoToDocumentDetails,
  handleToggleDocumentSelection,
  handleOpenDeleteDocumentDataModal
}: {
  documentData: Document
  processingDocument: boolean
  userSession: Partial<UserSessionType>
  handleGoToDocumentDetails: () => void
  handleOpenDeleteDocumentDataModal: (selectedDocumentData: Document) => void
  handleToggleDocumentSelection: (selectedDocumentData: Document, selected: boolean) => void
}) => {
  const { t } = useTranslation(['documents-ready-management'])

  const [documentFileExt, setDocumentFileExt] = useState('')
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [documentNameWithoutFileExt, setDocumentNameWithoutFileExt] = useState('')

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  useEffect(() => {
    if (documentData) {
      let documentNameWithoutFileExtData = ''
      const documentNameWithoutFileExtArray = documentData.originalName.split('.')

      for (let i = 0; i < documentNameWithoutFileExtArray.length - 1; i++) {
        documentNameWithoutFileExtData += documentNameWithoutFileExtArray[i]
      }

      setDocumentNameWithoutFileExt(documentNameWithoutFileExtData)
      setDocumentFileExt(documentNameWithoutFileExtArray[documentNameWithoutFileExtArray.length - 1])
    }
  }, [documentData, t])

  return (
    <>
      <div
        onClick={handleGoToDocumentDetails}
        className="documents-list-item-data-container"
      >
        <div className="documents-list-item-data-document-info">
          {processingDocument === false && (
            <Checkbox
              checked={documentData.selected ?? false}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => {
                handleToggleDocumentSelection(documentData, event.target.checked)
              }}
            />
          )}
          <div className="documents-list-item-data-document-info-name">{`${documentNameWithoutFileExt.toUpperCase()}.${documentFileExt}`}</div>
        </div>
        <div className="documents-list-item-beneficiaries-count-container">
          <div className="icon-container">
            <img
              src={GroupsIcon}
              alt="groups-icon"
            />
          </div>
          <div className="list-item-text">
            <div>{`${numberWithCommas(Number(documentData.totalOfNoveltiesReviewed))} ${
              Number(documentData.totalOfNoveltiesReviewed) === 1 ? t('listItem.beneficiarySingularText') : t('listItem.beneficiaryPluralText')
            }`}</div>
          </div>
        </div>
        <div className="documents-list-item-actions-container">
          <IconButton
            edge="end"
            sx={{ color: '#107569' }}
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation()
              setAnchorEl(event.currentTarget)
            }}
            aria-label="toggle password visibility"
          >
            <img
              src={MoreMenuIcon}
              alt="more-menu-icon"
            />
          </IconButton>
          {userSession?.role === 'admin' && processingDocument === false && (
            <IconButton
              edge="end"
              sx={{ color: '#107569' }}
              onClick={(event) => {
                event.stopPropagation()
                handleOpenDeleteDocumentDataModal(documentData)
              }}
              aria-label="toggle password visibility"
            >
              <img
                src={TrashIcon}
                alt="trash-icon"
              />
            </IconButton>
          )}
          <div className="documents-list-item-arrow-right">
            <img
              src={ArrowRightIcon}
              alt="arrow-right-icon"
            />
          </div>
        </div>
      </div>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation()
          setAnchorEl(null)
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <div className="documents-list-item-popover-container">
          <div className="documents-list-item-popover">
            <strong>{t('listItem.uploaded')}</strong>
            <div>{format(documentData.createdAt, 'dd/MM/yyyy | HH:mm:ss')}</div>
          </div>
          <Divider />
          <div className="documents-list-item-popover">
            <strong>{t('listItem.uploadedBy')}</strong>
            <div>{documentData.uploadedByFullName?.toUpperCase()}</div>
          </div>
        </div>
      </Popover>
    </>
  )
}

export default DocumentsReadyListItem
